/**
 * Add Session Modal Component
 * Supports single entry and bulk paste import modes
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  X, User, FileText, Plus, Check, AlertCircle, Trash2, Edit2, IndianRupee
} from 'lucide-react';
import { PRINCIPAL_RATE_PER_SESSION } from '../../hooks/useSessionHistory';
import DailyInsight from '../DailyInsight';
import CallAnalysis from '../CallAnalysis';

const AddSessionModal = ({ 
  filterOptions, 
  onAddSingle, 
  onClose,
  // Bulk import props
  bulkPasteData,
  setBulkPasteData,
  parseBulkPaste,
  parsedSessions,
  parseError,
  bulkImporting,
  updateParsedSession,
  removeParsedSession,
  importBulkSessions
}) => {
  const [addMode, setAddMode] = useState('single');
  const [saving, setSaving] = useState(false);
  const [addForm, setAddForm] = useState({
    childId: '',
    observerId: '',
    sessionDate: new Date().toISOString().split('T')[0],
    startedAt: '09:00',
    endedAt: '09:15',
    durationMinutes: 15,
    earnings: 75,
    notes: '',
    transcript: '',
    moodRating: 3,
    topicsDiscussed: '',
    keyObservations: '',
    concerns: ''
  });

  const handleAddSingle = async () => {
    setSaving(true);
    const success = await onAddSingle(addForm);
    setSaving(false);
    if (success) {
      onClose();
    }
  };

  const handleBulkImport = async () => {
    const success = await importBulkSessions();
    if (success) {
      onClose();
    }
  };

  const handleClose = () => {
    setAddMode('single');
    setBulkPasteData('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-3xl my-8">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Add Past Conversation(s)</CardTitle>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Mode Tabs */}
          <div className="flex gap-2 mt-4">
            <Button 
              variant={addMode === 'single' ? 'default' : 'outline'}
              onClick={() => setAddMode('single')}
              className={addMode === 'single' ? 'bg-blue-600' : ''}
              size="sm"
            >
              <User className="w-4 h-4 mr-2" /> Single Entry
            </Button>
            <Button 
              variant={addMode === 'bulk' ? 'default' : 'outline'}
              onClick={() => setAddMode('bulk')}
              className={addMode === 'bulk' ? 'bg-purple-600' : ''}
              size="sm"
            >
              <FileText className="w-4 h-4 mr-2" /> Bulk Paste Import
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4 max-h-[70vh] overflow-y-auto">
          {/* SINGLE ENTRY MODE */}
          {addMode === 'single' && (
            <SingleEntryForm 
              addForm={addForm}
              setAddForm={setAddForm}
              filterOptions={filterOptions}
              saving={saving}
              onSubmit={handleAddSingle}
              onCancel={handleClose}
            />
          )}
          
          {/* BULK PASTE MODE */}
          {addMode === 'bulk' && (
            <BulkPasteForm
              bulkPasteData={bulkPasteData}
              setBulkPasteData={setBulkPasteData}
              parseBulkPaste={parseBulkPaste}
              parsedSessions={parsedSessions}
              parseError={parseError}
              bulkImporting={bulkImporting}
              filterOptions={filterOptions}
              updateParsedSession={updateParsedSession}
              removeParsedSession={removeParsedSession}
              onImport={handleBulkImport}
              onCancel={handleClose}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Single Entry Form Sub-component
const SingleEntryForm = ({ addForm, setAddForm, filterOptions, saving, onSubmit, onCancel }) => {
  return (
    <>
      {/* Basic Info Section */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
          <User className="w-4 h-4" /> Session Details
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Child *</label>
            <select
              value={addForm.childId}
              onChange={(e) => setAddForm({...addForm, childId: e.target.value})}
              className="w-full border rounded-md px-3 py-2"
              required
              data-testid="add-session-child-select"
            >
              <option value="">Select Child</option>
              {filterOptions.children.map(child => (
                <option key={child.id} value={child.id}>{child.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="text-sm font-medium">Observer *</label>
            <select
              value={addForm.observerId}
              onChange={(e) => setAddForm({...addForm, observerId: e.target.value})}
              className="w-full border rounded-md px-3 py-2"
              required
              data-testid="add-session-observer-select"
            >
              <option value="">Select Observer</option>
              {filterOptions.observers.map(obs => (
                <option key={obs.id} value={obs.id}>{obs.name}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-4 mt-3">
          <div>
            <label className="text-sm font-medium">Date *</label>
            <Input
              type="date"
              value={addForm.sessionDate}
              onChange={(e) => setAddForm({...addForm, sessionDate: e.target.value})}
              required
              data-testid="add-session-date"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Start Time</label>
            <Input
              type="time"
              value={addForm.startedAt}
              onChange={(e) => setAddForm({...addForm, startedAt: e.target.value})}
            />
          </div>
          <div>
            <label className="text-sm font-medium">End Time</label>
            <Input
              type="time"
              value={addForm.endedAt}
              onChange={(e) => setAddForm({...addForm, endedAt: e.target.value})}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div>
            <label className="text-sm font-medium">Duration (minutes)</label>
            <Input
              type="number"
              value={addForm.durationMinutes}
              onChange={(e) => setAddForm({...addForm, durationMinutes: parseInt(e.target.value) || 0})}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Observer Earnings (₹)</label>
            <Input
              type="number"
              value={addForm.earnings}
              onChange={(e) => setAddForm({...addForm, earnings: parseFloat(e.target.value) || 0})}
            />
            <p className="text-xs text-gray-500 mt-1">Principal earns ₹{PRINCIPAL_RATE_PER_SESSION} per session</p>
          </div>
        </div>
      </div>

      {/* Conversation Section */}
      <div className="bg-purple-50 p-4 rounded-lg">
        <h4 className="font-medium text-purple-800 mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4" /> Conversation Details
        </h4>
        
        <div>
          <label className="text-sm font-medium">Full Conversation Transcript</label>
          <textarea
            value={addForm.transcript}
            onChange={(e) => setAddForm({...addForm, transcript: e.target.value})}
            className="w-full border rounded-md px-3 py-2 font-mono text-sm"
            rows={8}
            placeholder="Observer: Hello! How was your day today?&#10;Child: It was good! I played with my friends.&#10;Observer: That sounds fun! What did you play?&#10;Child: We played football...&#10;&#10;(Paste the full conversation here)"
            data-testid="add-session-transcript"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div>
            <label className="text-sm font-medium">Child's Mood (1-5)</label>
            <div className="flex gap-2 mt-1">
              {[1, 2, 3, 4, 5].map(rating => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => setAddForm({...addForm, moodRating: rating})}
                  className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-lg transition-all ${
                    addForm.moodRating === rating 
                      ? 'border-purple-500 bg-purple-100' 
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  {rating === 1 ? '😢' : rating === 2 ? '😔' : rating === 3 ? '😐' : rating === 4 ? '🙂' : '😊'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Topics Discussed</label>
            <Input
              value={addForm.topicsDiscussed}
              onChange={(e) => setAddForm({...addForm, topicsDiscussed: e.target.value})}
              placeholder="school, friends, hobbies, family"
              data-testid="add-session-topics"
            />
          </div>
        </div>
        
        <div className="mt-3">
          <label className="text-sm font-medium">Key Observations</label>
          <textarea
            value={addForm.keyObservations}
            onChange={(e) => setAddForm({...addForm, keyObservations: e.target.value})}
            className="w-full border rounded-md px-3 py-2"
            rows={2}
            placeholder="Child seemed happy today, talked about making new friends..."
          />
        </div>
        
        <div className="mt-3">
          <label className="text-sm font-medium">Any Concerns?</label>
          <textarea
            value={addForm.concerns}
            onChange={(e) => setAddForm({...addForm, concerns: e.target.value})}
            className="w-full border rounded-md px-3 py-2"
            rows={2}
            placeholder="Leave empty if no concerns..."
          />
        </div>
      </div>

      <div>
        <label className="text-sm font-medium">Admin Notes (Internal)</label>
        <textarea
          value={addForm.notes}
          onChange={(e) => setAddForm({...addForm, notes: e.target.value})}
          className="w-full border rounded-md px-3 py-2"
          rows={2}
          placeholder="Reason for manual entry..."
        />
      </div>
      
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button 
          onClick={onSubmit} 
          disabled={saving || !addForm.childId || !addForm.observerId}
          className="bg-green-600 hover:bg-green-700"
          data-testid="add-session-submit"
        >
          {saving ? 'Adding...' : 'Add Session'}
        </Button>
      </div>
    </>
  );
};

// Bulk Paste Form Sub-component
const BulkPasteForm = ({
  bulkPasteData,
  setBulkPasteData,
  parseBulkPaste,
  parsedSessions,
  parseError,
  bulkImporting,
  filterOptions,
  updateParsedSession,
  removeParsedSession,
  onImport,
  onCancel
}) => {
  return (
    <>
      {/* Instructions */}
      <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
        <h4 className="font-medium text-amber-800 mb-2">How to Paste Data</h4>
        <p className="text-sm text-amber-700 mb-2">
          Paste your session data in this format (tab-separated):
        </p>
        <code className="block bg-amber-100 p-2 rounded text-xs font-mono text-amber-900">
          ChildName[TAB]ObserverName[TAB]DD-MM-YYYY[TAB]{`{"transcript": "...", "report": "..."}`}
        </code>
        <p className="text-xs text-amber-600 mt-2">
          The system will auto-match names to existing children/observers and calculate earnings.
        </p>
      </div>
      
      {/* Paste Area */}
      <div>
        <label className="text-sm font-medium">Paste Session Data</label>
        <textarea
          value={bulkPasteData}
          onChange={(e) => {
            setBulkPasteData(e.target.value);
            parseBulkPaste(e.target.value);
          }}
          className="w-full border rounded-md px-3 py-2 font-mono text-xs"
          rows={6}
          placeholder='Virat        Kavita  27-11-2025      {"transcript": "Hello...", "report": "..."}'
          data-testid="bulk-paste-input"
        />
      </div>
      
      {/* Parse Error */}
      {parseError && (
        <div className="bg-red-50 border border-red-200 p-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-sm text-red-700">{parseError}</p>
        </div>
      )}
      
      {/* Parsed Sessions - EDITABLE Preview */}
      {parsedSessions.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-800 flex items-center gap-2">
              <Edit2 className="w-4 h-4" />
              Review & Edit Sessions ({parsedSessions.length})
            </h4>
            <div className="text-sm text-gray-600">
              Total: <span className="font-medium text-green-600">₹{parsedSessions.reduce((sum, s) => sum + s.totalEarnings, 0).toLocaleString()}</span>
            </div>
          </div>
          
          {/* Editable Session Cards */}
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {parsedSessions.map((session, idx) => (
              <ParsedSessionCard
                key={idx}
                session={session}
                index={idx}
                filterOptions={filterOptions}
                onUpdate={updateParsedSession}
                onRemove={removeParsedSession}
              />
            ))}
          </div>
          
          {/* Import Button */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
            <Button 
              onClick={onImport}
              disabled={bulkImporting || parsedSessions.filter(s => s.childId && s.observerId && s.sessionDate).length === 0}
              className="bg-green-600 hover:bg-green-700"
              data-testid="bulk-import-submit"
            >
              {bulkImporting ? (
                <>
                  <Plus className="w-4 h-4 mr-2 animate-spin" /> Importing...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" /> 
                  Import {parsedSessions.filter(s => s.childId && s.observerId && s.sessionDate).length} Sessions
                </>
              )}
            </Button>
          </div>
        </div>
      )}
      
      {/* Show cancel if no parsed sessions */}
      {parsedSessions.length === 0 && (
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
        </div>
      )}
    </>
  );
};

// Parsed Session Card Sub-component
const ParsedSessionCard = ({ session, index, filterOptions, onUpdate, onRemove }) => {
  const [showPreview, setShowPreview] = useState(false);
  
  return (
    <div 
      className={`border rounded-lg p-4 ${
        session.childId && session.observerId && session.sessionDate
          ? 'bg-green-50 border-green-200' 
          : 'bg-amber-50 border-amber-200'
      }`}
    >
      {/* Session Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700">
          Session #{index + 1}
          {session.transcript && (
            <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
              Has Transcript
            </span>
          )}
        </span>
        <div className="flex items-center gap-2">
          {session.childId && session.observerId && session.sessionDate ? (
            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
              <Check className="w-3 h-3" /> Ready
            </span>
          ) : (
            <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> Needs Edit
            </span>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onRemove(index)}
            className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Editable Fields */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* Child Selector */}
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">
            Child {!session.childId && <span className="text-red-500">*</span>}
          </label>
          <select
            value={session.childId}
            onChange={(e) => onUpdate(index, 'childId', e.target.value)}
            className={`w-full text-sm border rounded px-2 py-1.5 ${
              session.childId ? 'border-green-300 bg-white' : 'border-red-300 bg-red-50'
            }`}
          >
            <option value="">-- Select Child --</option>
            {filterOptions.children.map(child => (
              <option key={child.id} value={child.id}>{child.name}</option>
            ))}
          </select>
          {session.childName && !session.childId && (
            <p className="text-xs text-amber-600 mt-1">Detected: "{session.childName}"</p>
          )}
        </div>
        
        {/* Observer Selector */}
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">
            Observer {!session.observerId && <span className="text-red-500">*</span>}
          </label>
          <select
            value={session.observerId}
            onChange={(e) => onUpdate(index, 'observerId', e.target.value)}
            className={`w-full text-sm border rounded px-2 py-1.5 ${
              session.observerId ? 'border-green-300 bg-white' : 'border-red-300 bg-red-50'
            }`}
          >
            <option value="">-- Select Observer --</option>
            {filterOptions.observers.map(obs => (
              <option key={obs.id} value={obs.id}>{obs.name}</option>
            ))}
          </select>
          {session.observerName && !session.observerId && (
            <p className="text-xs text-amber-600 mt-1">Detected: "{session.observerName}"</p>
          )}
        </div>
        
        {/* Date */}
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">
            Date {!session.sessionDate && <span className="text-red-500">*</span>}
          </label>
          <Input
            type="date"
            value={session.sessionDate}
            onChange={(e) => onUpdate(index, 'sessionDate', e.target.value)}
            className={`text-sm h-8 ${
              session.sessionDate ? 'border-green-300' : 'border-red-300 bg-red-50'
            }`}
          />
        </div>
        
        {/* Earnings */}
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block flex items-center gap-1">
            <IndianRupee className="w-3 h-3" /> Earnings
          </label>
          <div className="flex gap-1">
            <Input
              type="number"
              value={session.observerEarnings}
              onChange={(e) => onUpdate(index, 'observerEarnings', e.target.value)}
              className="text-sm h-8 w-20"
              placeholder="Observer"
            />
            <span className="text-gray-400 self-center">+</span>
            <Input
              type="number"
              value={session.principalEarnings}
              onChange={(e) => onUpdate(index, 'principalEarnings', e.target.value)}
              className="text-sm h-8 w-16"
              placeholder="Principal"
            />
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Total: ₹{session.totalEarnings} ({session.durationMinutes} min)
          </p>
        </div>
      </div>
      
      {/* Preview Toggle */}
      {(session.report || session.callAnalysis) && (
        <div className="mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
            className="text-xs"
          >
            {showPreview ? 'Hide Preview' : 'Show Report Preview'}
          </Button>
          
          {showPreview && (
            <div className="mt-3 space-y-4">
              {session.report && (
                <DailyInsight 
                  content={session.report}
                  childName={session.childName || 'Child'}
                  sessionDate={session.sessionDate}
                />
              )}
              {session.callAnalysis && (
                <CallAnalysis 
                  content={session.callAnalysis}
                  observerName={session.observerName || 'Observer'}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddSessionModal;
